/**
 * InterAIA / Porto OS design system
 * FC Porto–inspired — navy blue + gold on light paper background.
 * Stack: Tailwind CSS + shadcn/ui (HSL CSS variables). Dark mode: `.dark` class.
 */

export const interOsTheme = {
  name: 'InterAIA / Porto OS',
  fonts: {
    heading: "'Almarai', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    body: "'Almarai', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    display: "'Almarai', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    mono: "'JetBrains Mono', ui-monospace, Menlo, monospace",
  },
  brandColors: {
    blue: '#0A4DA2',
    blueDeep: '#002B5C',
    blueMid: '#063e85',
    blueInk: '#00203F',
    gold: '#C9A24B',
    goldDeep: '#9A7826',
    paper: '#EEF1F5',
    cream: '#FBFCFE',
    ink: '#13202E',
    muted: '#6A7280',
    line: '#E3E7ED',
    lineSoft: '#EDF0F4',
  },
  semanticColors: {
    success: { text: '#1F9D6B', background: '#E7F5EE' },
    warning: { text: '#C9802B', background: '#FBF1E2' },
    error: { text: '#C24B4B', background: '#FBECEC' },
  },
  agentColors: {
    jose: '#0A4DA2',
    jorge: '#C9A24B',
    val: '#002B5C',
    ava: '#9A7826',
    fan: '#aeb5bf',
  },
  layout: {
    mainPadding: '26px 30px 60px',
    mainBackground: '#EEF1F5',
    topBarHeight: '56px',
    borderRadius: '0.75rem',
    sidebarBackground: '#FFFFFF',
    sidebarBorder: '#E3E7ED',
    activeNavBackground: '#0A4DA2',
    activeNavText: '#FFFFFF',
    navHoverBackground: '#EDF0F4',
  },
  gradients: {
    topBar: 'linear-gradient(90deg, #00203F, #002B5C 60%, #063e85)',
    heroCard: 'linear-gradient(135deg, var(--blue-deep) 0%, var(--blue) 70%)',
    badgeLogo: 'linear-gradient(135deg, #0A4DA2, #063e85)',
  },
  chartColors: {
    chart1: '213 88% 34%',
    chart2: '40 50% 54%',
    chart3: '155 66% 37%',
    chart4: '35 60% 48%',
    chart5: '0 60% 52%',
  },
};

export const portoOsLightTokens = `
  /* shadcn HSL tokens — light */
  --background: 220 20% 95%;
  --foreground: 210 40% 13%;
  --card: 0 0% 100%;
  --card-foreground: 210 40% 13%;
  --popover: 0 0% 100%;
  --popover-foreground: 210 40% 13%;
  --primary: 213 88% 34%;
  --primary-foreground: 0 0% 100%;
  --secondary: 220 20% 95%;
  --secondary-foreground: 210 40% 13%;
  --muted: 220 14% 95%;
  --muted-foreground: 218 10% 46%;
  --accent: 40 50% 54%;
  --accent-foreground: 210 100% 12%;
  --destructive: 0 60% 52%;
  --destructive-foreground: 0 0% 98%;
  --border: 220 16% 90%;
  --input: 220 16% 90%;
  --ring: 213 88% 34%;
  --radius: 0.75rem;

  /* fonts */
  --font-heading: ${interOsTheme.fonts.heading};
  --font-body: ${interOsTheme.fonts.body};
  --font-display: ${interOsTheme.fonts.display};
  --font-mono: ${interOsTheme.fonts.mono};
  --font-sans: ${interOsTheme.fonts.body};

  /* brand */
  --blue: ${interOsTheme.brandColors.blue};
  --blue-deep: ${interOsTheme.brandColors.blueDeep};
  --blue-mid: ${interOsTheme.brandColors.blueMid};
  --blue-ink: ${interOsTheme.brandColors.blueInk};
  --gold: ${interOsTheme.brandColors.gold};
  --gold-deep: ${interOsTheme.brandColors.goldDeep};
  --paper: ${interOsTheme.brandColors.paper};
  --ink: ${interOsTheme.brandColors.ink};
  --fc-muted: ${interOsTheme.brandColors.muted};
  --line: ${interOsTheme.brandColors.line};
  --line-soft: ${interOsTheme.brandColors.lineSoft};
  --cream: ${interOsTheme.brandColors.cream};

  /* semantic */
  --ok: ${interOsTheme.semanticColors.success.text};
  --ok-bg: ${interOsTheme.semanticColors.success.background};
  --warn: ${interOsTheme.semanticColors.warning.text};
  --warn-bg: ${interOsTheme.semanticColors.warning.background};
  --bad: ${interOsTheme.semanticColors.error.text};
  --bad-bg: ${interOsTheme.semanticColors.error.background};

  /* agent colors */
  --agent-jose: ${interOsTheme.agentColors.jose};
  --agent-jorge: ${interOsTheme.agentColors.jorge};
  --agent-val: ${interOsTheme.agentColors.val};
  --agent-ava: ${interOsTheme.agentColors.ava};
  --agent-fan: ${interOsTheme.agentColors.fan};

  /* chart */
  --chart-1: ${interOsTheme.chartColors.chart1};
  --chart-2: ${interOsTheme.chartColors.chart2};
  --chart-3: ${interOsTheme.chartColors.chart3};
  --chart-4: ${interOsTheme.chartColors.chart4};
  --chart-5: ${interOsTheme.chartColors.chart5};

  /* gradients */
  --gradient-top-bar: ${interOsTheme.gradients.topBar};
  --gradient-hero-card: ${interOsTheme.gradients.heroCard};
  --gradient-badge-logo: ${interOsTheme.gradients.badgeLogo};
`;

export const portoOsDarkTokens = `
  --background: 210 40% 6%;
  --foreground: 0 0% 95%;
  --card: 210 40% 10%;
  --card-foreground: 0 0% 95%;
  --popover: 210 40% 10%;
  --popover-foreground: 0 0% 95%;
  --primary: 213 80% 55%;
  --primary-foreground: 0 0% 100%;
  --secondary: 210 30% 15%;
  --secondary-foreground: 0 0% 95%;
  --muted: 210 30% 15%;
  --muted-foreground: 218 10% 60%;
  --accent: 40 50% 54%;
  --accent-foreground: 0 0% 100%;
  --destructive: 0 62% 30%;
  --destructive-foreground: 0 0% 98%;
  --border: 210 30% 18%;
  --input: 210 30% 18%;
  --ring: 213 80% 55%;
`;

/** Legacy aliases for styled-components — maps to brand tokens only, no new colors */
export const portoOsLegacyAliases = `
  --body-bg: var(--paper);
  --sidebar-bg: ${interOsTheme.layout.sidebarBackground};
  --card-bg: #FFFFFF;
  --header-bg: var(--blue-deep);
  --white: #FFFFFF;
  --off-white: var(--cream);

  --text-color: var(--ink);
  --text-body: var(--fc-muted);
  --text-color-50: var(--fc-muted);
  --light-gray: var(--fc-muted);
  --black: var(--ink);
  --dark: var(--ink);
  --base-text-color: var(--ink);

  --primary-hover: var(--blue-mid);
  --primary-50: var(--line-soft);
  --link-color: var(--blue);
  --turquoise: var(--blue);

  --success: var(--ok);
  --success-bg: var(--ok-bg);
  --green: var(--ok);
  --warning: var(--warn);
  --warning-bg: var(--warn-bg);
  --danger: var(--bad);
  --danger-bg: var(--bad-bg);
  --light: var(--cream);
  --yellow: var(--gold);
  --gold-muted: var(--gold-deep);

  --gray: var(--paper);
  --gray-50: var(--line-soft);
  --gray-100: var(--line);
  --gray-150: var(--line);
  --table-border: var(--line);

  --toastify-color-success: var(--ok);
  --toastify-icon-color-success: var(--ok);
  --toastify-color-error: var(--bad);
  --toastify-icon-color-error: var(--bad);
  --toastify-font-family: var(--font-body);

  --base-font-sans-serif: var(--font-body);
  --base-font-serif: var(--font-body);
  --base-background-color: var(--paper);
  --base-font-family: var(--font-body);
  --headings-font-family: var(--font-heading);
  --headings-color: var(--ink);

  --icon-muted-filter: brightness(0) saturate(100%) invert(44%) sepia(10%) saturate(650%) hue-rotate(182deg);
  --icon-primary-filter: brightness(0) saturate(100%) invert(24%) sepia(90%) saturate(1200%) hue-rotate(186deg) brightness(95%);
`;

export const interOsCssVariables = portoOsLightTokens;
